import { createElement, Fragment } from "react";
/**
 * WordPress dependencies
 */
import { privateApis as componentsPrivateApis, Button, Modal } from '@wordpress/components';
import { __, sprintf, _n } from '@wordpress/i18n';
import { useMemo, useState, useCallback, useEffect } from '@wordpress/element';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
const {
  DropdownMenuV2: DropdownMenu,
  DropdownMenuGroupV2: DropdownMenuGroup,
  DropdownMenuItemV2: DropdownMenuItem,
  DropdownMenuSeparatorV2: DropdownMenuSeparator
} = unlock(componentsPrivateApis);
export function useHasAPossibleBulkAction(actions, item) {
  return useMemo(() => {
    return actions.some(action => {
      return action.supportsBulk && (!action.isEligible || action.isEligible(item));
    });
  }, [actions, item]);
}
export function useSomeItemHasAPossibleBulkAction(actions, data) {
  return useMemo(() => {
    return data.some(item => {
      return actions.some(action => {
        return action.supportsBulk && (!action.isEligible || action.isEligible(item));
      });
    });
  }, [actions, data]);
}
function ActionWithModal({
  action,
  selectedItems,
  setActionWithModal,
  onMenuOpenChange
}) {
  const eligibleItems = useMemo(() => {
    return selectedItems.filter(item => !action.isEligible || action.isEligible(item));
  }, [action, selectedItems]);
  const {
    RenderModal,
    hideModalHeader
  } = action;
  const onCloseModal = useCallback(() => {
    setActionWithModal(undefined);
  }, [setActionWithModal]);
  return createElement(Modal, {
    title: !hideModalHeader ? action.label : undefined,
    __experimentalHideHeader: !!hideModalHeader,
    onRequestClose: onCloseModal,
    overlayClassName: "dataviews-action-modal"
  }, createElement(RenderModal, {
    items: eligibleItems,
    closeModal: onCloseModal,
    onActionPerformed: () => onMenuOpenChange(false)
  }));
}
function BulkActionItem({
  action,
  selectedItems,
  setActionWithModal
}) {
  const eligibleItems = useMemo(() => {
    return selectedItems.filter(item => !action.isEligible || action.isEligible(item));
  }, [action, selectedItems]);
  const shouldShowModal = ('RenderModal' in action);
  return createElement(DropdownMenuItem, {
    key: action.id,
    disabled: eligibleItems.length === 0,
    hideOnClick: !shouldShowModal,
    onClick: async () => {
      if (shouldShowModal) {
        setActionWithModal(action);
      } else {
        await action.callback(eligibleItems);
      }
    },
    suffix: eligibleItems.length > 0 ? eligibleItems.length : undefined
  }, action.label);
}
function ActionsMenuGroup({
  actions,
  selectedItems,
  setActionWithModal
}) {
  return createElement(Fragment, null, createElement(DropdownMenuGroup, null, actions.map(action => createElement(BulkActionItem, {
    key: action.id,
    action: action,
    selectedItems: selectedItems,
    setActionWithModal: setActionWithModal
  }))), createElement(DropdownMenuSeparator, null));
}
export default function BulkActions({
  data,
  actions,
  selection,
  onSelectionChange,
  getItemId
}) {
  const bulkActions = useMemo(() => actions.filter(action => action.supportsBulk), [actions]);
  const [isMenuOpen, onMenuOpenChange] = useState(false);
  const [actionWithModal, setActionWithModal] = useState();
  const selectableItems = useMemo(() => {
    return data.filter(item => {
      return bulkActions.some(action => !action.isEligible || action.isEligible(item));
    });
  }, [data, bulkActions]);
  const numberSelectableItems = selectableItems.length;
  const areAllSelected = selection && selection.length === numberSelectableItems;
  const selectedItems = useMemo(() => {
    return data.filter(item => selection.includes(getItemId(item)));
  }, [selection, data, getItemId]);
  const hasNonSelectableItemSelected = useMemo(() => {
    return selectedItems.some(item => {
      return !selectableItems.includes(item);
    });
  }, [selectedItems, selectableItems]);
  useEffect(() => {
    if (hasNonSelectableItemSelected) {
      onSelectionChange(selectedItems.filter(selectedItem => {
        return selectableItems.some(item => {
          return getItemId(selectedItem) === getItemId(item);
        });
      }));
    }
  }, [hasNonSelectableItemSelected, selectedItems, selectableItems, getItemId, onSelectionChange]);
  if (bulkActions.length === 0) {
    return null;
  }
  return createElement(Fragment, null, createElement(DropdownMenu, {
    open: isMenuOpen,
    onOpenChange: onMenuOpenChange,
    label: __('Bulk actions'),
    style: {
      minWidth: '240px'
    },
    trigger: createElement(Button, {
      className: "dataviews-bulk-edit-button",
      __next40pxDefaultSize: true,
      variant: "tertiary",
      size: "compact"
    }, selection.length ? sprintf( /* translators: %d: Number of items. */
    _n('Edit %d item', 'Edit %d items', selection.length), selection.length) : __('Bulk edit'))
  }, createElement(ActionsMenuGroup, {
    actions: bulkActions,
    setActionWithModal: setActionWithModal,
    selectedItems: selectedItems
  }), createElement(DropdownMenuGroup, null, createElement(DropdownMenuItem, {
    disabled: areAllSelected,
    hideOnClick: false,
    onClick: () => {
      onSelectionChange(selectableItems);
    },
    suffix: numberSelectableItems
  }, __('Select all')), createElement(DropdownMenuItem, {
    disabled: selection.length === 0,
    hideOnClick: false,
    onClick: () => {
      onSelectionChange([]);
    }
  }, __('Deselect')))), actionWithModal && createElement(ActionWithModal, {
    action: actionWithModal,
    selectedItems: selectedItems,
    setActionWithModal: setActionWithModal,
    onMenuOpenChange: onMenuOpenChange
  }));
}
//# sourceMappingURL=bulk-actions.js.map